import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, MenuItem, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Typography,
} from '@mui/material';
import {
  ArrowForward, Blender, Build, CheckCircle, ErrorOutline, Factory, Inventory2, LocalShipping, MoveToInbox, PostAdd,
  PrecisionManufacturing, ReceiptLong, Schedule, Scale as ScaleIcon, WarningAmber, Widgets,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import type { DifferenceTreatment, MaintenancePlan, Machine, MaterialLot, Reception, ShiftEntry } from '../../../types/factory';
import { StatusPill, TONES, Tone } from '../../../components/ui/PageKit';
import { addDaysIso, errorMessage, fmtMoney, fmtPct, fmtQty, num } from '../shared';
import type { PlantLayout, PlantZone } from './layouts';
import {
  PlantData, ZoneStatus, fmtDayShort, fmtKg, fmtWhen, findMachine, isMold, locationsOf, materialsOf, molds, pendingDocs,
  productsOf, shiftsToday, worstLevel,
} from './data';
import {
  ACCENT, AnimatedNumber, Callout, HeroFigure, INK, LINE, MUTED, MiniBars, PanelShell, PanelTitle, ProgressBar, PulseDot,
  SURFACE, Stagger, StatBox, accentButton, outlineButton,
} from './ui';

export interface PanelProps {
  zone: PlantZone;
  data: PlantData;
  status: ZoneStatus;
  onClose: () => void;
  onSelect: (id: string) => void;
  onSaved: () => void;
}

const SERIES = ['#2F6BFF', '#16A36A', ACCENT, '#8B5CF6', '#E59A0C', '#0EA5B7', '#D9467A', '#64748B'];
const SHIFT_ORDER: Record<string, number> = { TM: 0, TT: 1, TN: 2 };
const SHIFT_HOUR: Record<string, string> = { TM: '06', TT: '14', TN: '22' };
const shiftAt = (s: ShiftEntry) => new Date(`${s.date.slice(0, 10)}T${SHIFT_HOUR[s.shift] || '12'}:00:00-03:00`).toISOString();

const Footer: React.FC<{ primary?: { label: string; icon?: React.ReactNode; onClick: () => void; disabled?: boolean }; secondary?: { label: string; icon?: React.ReactNode; onClick: () => void } }> = ({ primary, secondary }) => (
  <Stack direction="row" spacing={1.5}>
    {primary && <Button fullWidth variant="contained" size="large" startIcon={primary.icon} onClick={primary.onClick} disabled={primary.disabled} sx={accentButton}>{primary.label}</Button>}
    {secondary && <Button fullWidth variant="outlined" size="large" startIcon={secondary.icon} onClick={secondary.onClick} sx={outlineButton}>{secondary.label}</Button>}
  </Stack>
);

const KeyValue: React.FC<{ rows: [React.ReactNode, React.ReactNode][] }> = ({ rows }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 2, rowGap: 0.75 }}>
    {rows.map(([k, v], i) => (
      <React.Fragment key={i}>
        <Typography variant="body2" sx={{ color: MUTED }}>{k}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, color: INK }}>{v}</Typography>
      </React.Fragment>
    ))}
  </Box>
);

const Missing: React.FC<{ title: string; hint: string; action?: { label: string; path: string } }> = ({ title, hint, action }) => {
  const navigate = useNavigate();
  return (
    <Box sx={{ mt: 2, bgcolor: SURFACE, borderRadius: 3, px: 2.5, py: 3, textAlign: 'center' }}>
      <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
      <Typography variant="body2" sx={{ color: MUTED, mt: 0.5, maxWidth: '46ch', mx: 'auto' }}>{hint}</Typography>
      {action && <Button sx={{ ...outlineButton, mt: 2, py: 0.75 }} variant="outlined" onClick={() => navigate(action.path)}>{action.label}</Button>}
    </Box>
  );
};

const toneLabel: Record<Tone, string> = { ok: 'Al día', warn: 'Atención', bad: 'Urgente', info: 'Info', neutral: '—' };
const StatusBadge: React.FC<{ status: ZoneStatus }> = ({ status }) => (
  <StatusPill tone={status.tone}>{status.label || toneLabel[status.tone]}</StatusPill>
);

// ==================== Máquina (inyectoras, sopladora) ====================

export const MachinePanel: React.FC<PanelProps> = ({ zone, data, status, onClose }) => {
  const navigate = useNavigate();
  const m = findMachine(zone, data.machines);
  const ov = m ? data.overview.find(o => o.machineId === m.id) : undefined;
  const today = m ? shiftsToday(data, m.id).sort((a, b) => SHIFT_ORDER[a.shift] - SHIFT_ORDER[b.shift]) : [];
  const recent = m ? data.shifts.filter(s => s.machineId === m.id).sort((a, b) => shiftAt(b).localeCompare(shiftAt(a))) : [];
  const last = recent[0];
  const mounted = m ? data.machines.find(x => isMold(x) && x.mountedOnId === m.id) : undefined;
  const good = today.reduce((a, s) => a + s.goodQuantity, 0);
  const scrap = today.reduce((a, s) => a + s.scrapQuantity, 0);
  const expected = today.reduce((a, s) => a + (s.expectedQuantity || 0), 0);
  const days = Array.from({ length: 7 }, (_, i) => addDaysIso(data.today, i - 6)).map(d => ({
    label: fmtDayShort(d), value: data.shifts.filter(s => s.machineId === m?.id && s.date.slice(0, 10) === d).reduce((a, s) => a + s.goodQuantity, 0),
  }));

  return (
    <PanelShell
      title={m?.name || zone.label}
      subtitle={m ? [m.kind, m.code].filter(Boolean).join(' · ') || 'Máquina' : 'Máquina'}
      badge={<StatusBadge status={status} />}
      onClose={onClose}
      footer={m && <Footer
        primary={{ label: 'Registrar producción', icon: <PostAdd />, onClick: () => navigate('/factory/production') }}
        secondary={{ label: 'Registrar parada', icon: <Build />, onClick: () => navigate('/factory/maintenance') }}
      />}
    >
      {!m ? (
        <Missing title={`No hay una máquina cargada como “${zone.label}”`} hint="Cargala en Producción → Máquinas con ese nombre y este sector empieza a mostrar sus partes, su rendimiento y su mantenimiento." action={{ label: 'Cargar máquinas', path: '/factory/production' }} />
      ) : (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1.35fr 1fr' }, gap: 1, mt: 2 }}>
            <HeroFigure value={good} caption="Unidades buenas hoy" tone={good ? 'accent' : 'neutral'} />
            <Stack spacing={1}>
              <StatBox label="Objetivo de los partes" value={expected ? fmtQty(expected) : '—'} hint={expected ? `Faltan ${fmtQty(Math.max(0, expected - good))}` : 'Sin estándar cargado'} />
              <StatBox label="Rotos" value={fmtQty(scrap)} tone={scrap && good + scrap ? (scrap / (good + scrap) > 0.05 ? 'bad' : 'warn') : undefined}
                hint={good + scrap ? `${fmtPct(scrap / (good + scrap), 1)} del total` : undefined} />
            </Stack>
          </Box>
          {expected > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="body2" sx={{ color: MUTED }}>Avance contra el estándar</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}><AnimatedNumber value={Math.round((good / expected) * 100)} suffix=" %" /></Typography>
              </Stack>
              <ProgressBar value={(good / expected) * 100} color={good / expected >= 0.85 ? TONES.ok.dot : good / expected >= 0.6 ? '#E59A0C' : TONES.bad.dot} />
            </Box>
          )}

          <PanelTitle>Trabajo actual</PanelTitle>
          <KeyValue rows={[
            ['Producto', last?.productName ?? '—'],
            ['Matriz', mounted?.name ?? last?.moldName ?? '—'],
            ['Operario', last?.operatorName ?? '—'],
            ['Mezcla (lote)', last?.mixBatchCode ?? '—'],
            ['Último parte', last ? `${fmtDayShort(last.date)} · turno ${last.shift}` : 'Sin partes en la semana'],
          ]} />

          <PanelTitle>Rendimiento</PanelTitle>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1 }}>
            <StatBox label="Estándar" value={m.effectiveUnitsPerHour ? `${fmtQty(m.effectiveUnitsPerHour)} u/h` : '—'} />
            <StatBox label="Ciclo" value={m.cycleSeconds ? `${String(m.cycleSeconds).replace('.', ',')} s` : '—'} />
            <StatBox label="Cavidades" value={m.cavitiesTotal ? `${m.cavitiesUseful ?? m.cavitiesTotal}/${m.cavitiesTotal}` : '—'} hint="útiles / totales" />
          </Box>

          <PanelTitle>Últimos 7 días</PanelTitle>
          <MiniBars values={days} />

          {today.length > 0 && (
            <>
              <PanelTitle>Partes de hoy</PanelTitle>
              <Table size="small">
                <TableHead><TableRow><TableCell>Turno</TableCell><TableCell>Producto</TableCell><TableCell align="right">Buenas</TableCell><TableCell align="right">Rotos</TableCell><TableCell align="right">Efic.</TableCell></TableRow></TableHead>
                <TableBody>
                  {today.map(s => (
                    <TableRow key={s.id}>
                      <TableCell>{s.shift}</TableCell>
                      <TableCell>{s.productName}</TableCell>
                      <TableCell align="right">{fmtQty(s.goodQuantity)}</TableCell>
                      <TableCell align="right">{fmtQty(s.scrapQuantity)}</TableCell>
                      <TableCell align="right">{s.efficiencyPct != null ? fmtPct(s.efficiencyPct) : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}

          <PanelTitle>Mantenimiento</PanelTitle>
          {ov && ov.plansCount > 0 ? (
            <Callout
              tone={ov.maintenanceStatus === 'vencido' ? 'bad' : ov.maintenanceStatus === 'proximo' ? 'warn' : 'ok'}
              icon={ov.maintenanceStatus === 'vencido' ? <ErrorOutline fontSize="inherit" /> : ov.maintenanceStatus === 'proximo' ? <Schedule fontSize="inherit" /> : <CheckCircle fontSize="inherit" />}
              title={ov.maintenanceStatus === 'vencido' ? 'Mantenimiento vencido' : ov.maintenanceStatus === 'proximo' ? 'Mantenimiento próximo' : 'Mantenimiento al día'}
            >
              {ov.nextDueTask ? `${ov.nextDueTask}${ov.nextDueDate ? ` · ${fmtDayShort(ov.nextDueDate)}` : ''}` : 'Sin tareas pendientes'}
              {ov.nextScheduledDate ? ` · programado ${fmtDayShort(ov.nextScheduledDate)}` : ''}
              {` · ${fmtQty(ov.hours)} h de uso`}
            </Callout>
          ) : (
            <Typography variant="body2" sx={{ color: MUTED }}>Sin plan preventivo cargado (aceite, filtro, lubricación).</Typography>
          )}
        </>
      )}
    </PanelShell>
  );
};

// ==================== Stock de terminados ====================

export const ProductsPanel: React.FC<PanelProps> = ({ zone, data, status, onClose }) => {
  const navigate = useNavigate();
  const items = productsOf(zone, data).sort((a, b) => b.stock - a.stock);
  const locs = locationsOf(zone, data);
  const physical = items.reduce((a, i) => a + i.stock, 0);
  const reserved = items.reduce((a, i) => a + Math.max(0, i.pendingOrders), 0);
  const available = physical - reserved;
  const tone = worstLevel(items);
  const ids = new Set(items.map(i => i.id));
  const lastShift = data.shifts.filter(s => ids.has(s.productId)).sort((a, b) => shiftAt(b).localeCompare(shiftAt(a)))[0];
  const low = items.filter(i => i.level === 'amarillo' || i.level === 'rojo');
  const toProduce = items.filter(i => i.suggestedQuantity > 0);

  return (
    <PanelShell
      title={zone.label}
      subtitle={[locs.length ? `Ubicación ${locs.map(l => l.code).join(', ')}` : null, 'Producto terminado'].filter(Boolean).join(' · ')}
      onClose={onClose}
      footer={<Footer
        primary={{ label: 'Registrar movimiento', icon: <PostAdd />, onClick: () => navigate('/factory/storage') }}
        secondary={{ label: 'Ver stock', icon: <Inventory2 />, onClick: () => navigate('/factory/stock') }}
      />}
    >
      {!items.length ? (
        <Missing title="No hay productos para este sector" hint="Los productos se enganchan por nombre. Revisá que estén cargados en Productos." action={{ label: 'Ver stock', path: '/factory/stock' }} />
      ) : (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1.3fr 1fr 1fr' }, gap: 1, mt: 2, alignItems: 'stretch' }}>
            <HeroFigure value={available} caption="Disponibles" tone={available <= 0 ? 'bad' : 'accent'} />
            <StatBox label="Stock físico" value={<AnimatedNumber value={physical} />} />
            <StatBox label="Reservados" value={<AnimatedNumber value={reserved} />} hint="OC abiertas" />
          </Box>
          <Typography variant="caption" sx={{ color: MUTED, display: 'block', mt: 0.75 }}>Disponibles = físico − reservado por órdenes de compra abiertas</Typography>

          <PanelTitle>Por producto</PanelTitle>
          <Table size="small">
            <TableHead><TableRow><TableCell>Producto</TableCell><TableCell align="right">Físico</TableCell><TableCell align="right">Disponible</TableCell><TableCell align="right">Estado</TableCell></TableRow></TableHead>
            <TableBody>
              {items.map((i, idx) => (
                <TableRow key={i.id}>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: SERIES[idx % SERIES.length], flexShrink: 0 }} />
                      <span>{i.name}</span>
                    </Stack>
                  </TableCell>
                  <TableCell align="right">{fmtQty(i.stock)}</TableCell>
                  <TableCell align="right">{fmtQty(i.stock - Math.max(0, i.pendingOrders))}</TableCell>
                  <TableCell align="right">
                    <StatusPill tone={i.level === 'rojo' ? 'bad' : i.level === 'amarillo' ? 'warn' : i.level === 'verde' ? 'ok' : 'neutral'}>
                      {i.level === 'rojo' ? 'Sin stock' : i.level === 'amarillo' ? 'Bajo' : i.level === 'verde' ? 'OK' : 'Sin niveles'}
                    </StatusPill>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {tone === 'ok' && <Callout tone="ok" icon={<CheckCircle fontSize="inherit" />} title="Stock suficiente">Todos por encima del nivel de aviso.</Callout>}
          {(tone === 'warn' || tone === 'bad') && (
            <Callout tone={tone} icon={<WarningAmber fontSize="inherit" />} title={tone === 'bad' ? 'Hay productos sin stock' : 'Stock cerca del mínimo'}>
              {low.map(i => `${i.name} (mín. ${fmtQty(i.minStock)})`).join(' · ')}
            </Callout>
          )}
          {tone === 'neutral' && <Callout tone="neutral" title="Sin niveles configurados">Configurá mínimo y aviso en Stock y alertas para ver el semáforo.</Callout>}
          {toProduce.length > 0 && (
            <Typography variant="body2" sx={{ mt: 1.25 }}>
              <b>Para producir:</b> {toProduce.map(i => `${fmtQty(i.suggestedQuantity)} ${i.name}`).join(' · ')}
            </Typography>
          )}

          {locs.length > 0 && (
            <>
              <PanelTitle>Ubicaciones</PanelTitle>
              <Stack spacing={1.25}>
                {locs.map(l => (
                  <Box key={l.id}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" sx={{ fontWeight: 650 }}>{l.code}{l.name ? ` · ${l.name}` : ''}</Typography>
                      <Typography variant="body2" sx={{ color: MUTED }}>{fmtQty(l.occupied)}{l.capacity ? ` / ${fmtQty(l.capacity)} ${l.capacityUnit || ''}` : ''}</Typography>
                    </Stack>
                    {l.occupancyPct != null && <Box sx={{ mt: 0.5 }}><ProgressBar value={l.occupancyPct * 100} height={6} color={l.occupancyPct > 0.9 ? TONES.bad.dot : ACCENT} /></Box>}
                  </Box>
                ))}
              </Stack>
            </>
          )}

          <PanelTitle>Último movimiento</PanelTitle>
          {lastShift ? (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ bgcolor: SURFACE, borderRadius: 2, px: 2, py: 1.5 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: TONES.ok.bg, color: TONES.ok.dot, display: 'grid', placeItems: 'center' }}><MoveToInbox /></Box>
              <Box>
                <Typography sx={{ fontWeight: 700 }}>Ingreso de producción · +{fmtQty(lastShift.goodQuantity)} u.</Typography>
                <Typography variant="body2" sx={{ color: MUTED }}>{fmtDayShort(lastShift.date)} · turno {lastShift.shift} · {lastShift.productName}{lastShift.machineName ? ` · ${lastShift.machineName}` : ''}</Typography>
              </Box>
            </Stack>
          ) : <Typography variant="body2" sx={{ color: MUTED }}>Sin partes en la última semana.</Typography>}
        </>
      )}
    </PanelShell>
  );
};

// ==================== Materia prima (virgen, recuperado) e insumos ====================

export const MaterialsPanel: React.FC<PanelProps> = ({ zone, data, onClose, onSelect }) => {
  const navigate = useNavigate();
  const mats = materialsOf(zone, data).sort((a, b) => b.currentStock - a.currentStock);
  const ids = useMemo(() => new Set(mats.map(m => m.id)), [mats]);
  const [lots, setLots] = useState<MaterialLot[] | null>(null);
  const byKg = zone.kind === 'materials';
  const unit = byKg ? 'kg' : 'u.';

  useEffect(() => {
    let alive = true;
    api.getMaterialLots({ onlyAvailable: true }).then(l => { if (alive) setLots(l); }).catch(() => { if (alive) setLots([]); });
    return () => { alive = false; };
  }, [data.materials]);

  const myLots = (lots || []).filter(l => ids.has(l.materialId));
  const total = mats.reduce((a, m) => a + m.currentStock, 0);
  const levels = data.stock.materials.filter(i => ids.has(i.id));
  const tone = worstLevel(levels);
  const receptions = data.receptions.filter(r => ids.has(r.materialId)).slice(0, 5);
  const suppliers = new Set(receptions.map(r => r.supplierName).filter(Boolean));

  return (
    <PanelShell
      title={zone.label}
      subtitle={byKg ? `Materia prima por peso · ${zone.origins?.length === 1 ? 'virgen' : 'recuperada propia y comprada'}` : 'Insumos de armado'}
      onClose={onClose}
      footer={<Footer
        primary={byKg ? { label: 'Pesar ingreso', icon: <ScaleIcon />, onClick: () => onSelect('balanza') } : { label: 'Registrar movimiento', icon: <PostAdd />, onClick: () => navigate('/factory/storage') }}
        secondary={{ label: byKg ? 'Ver materia prima' : 'Ver stock', icon: <ArrowForward />, onClick: () => navigate(byKg ? '/factory/materials' : '/factory/stock') }}
      />}
    >
      {!mats.length ? (
        <Missing
          title={byKg ? 'No hay materias primas con ese origen' : 'No hay insumos para este sector'}
          hint={byKg ? 'En Materia prima, marcá el origen de cada MP (virgen, recuperada propia o comprada) y aparecen acá.' : 'Los insumos se enganchan por nombre (o-ring, alambre, manija).'}
          action={{ label: byKg ? 'Materia prima' : 'Ver stock', path: byKg ? '/factory/materials' : '/factory/stock' }}
        />
      ) : (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1.3fr 1fr 1fr' }, gap: 1, mt: 2 }}>
            <HeroFigure value={total} unit={unit} caption="Disponibles" tone={tone === 'bad' ? 'bad' : 'accent'} />
            <StatBox label="Lotes abiertos" value={lots ? myLots.length : '…'} hint="se usan del más viejo" />
            <StatBox label="Proveedores" value={suppliers.size || '—'} hint="últimos ingresos" />
          </Box>

          <PanelTitle>Por material</PanelTitle>
          <Table size="small">
            <TableHead><TableRow><TableCell>Material</TableCell><TableCell align="right">{byKg ? 'Kg' : 'Stock'}</TableCell><TableCell align="right">Estado</TableCell></TableRow></TableHead>
            <TableBody>
              {mats.map((m, idx) => {
                const lv = levels.find(l => l.id === m.id)?.level;
                return (
                  <TableRow key={m.id}>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: SERIES[idx % SERIES.length], flexShrink: 0 }} />
                        <span>{m.name}</span>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{fmtQty(m.currentStock)}</TableCell>
                    <TableCell align="right">
                      <StatusPill tone={lv === 'rojo' ? 'bad' : lv === 'amarillo' ? 'warn' : lv === 'verde' ? 'ok' : 'neutral'}>
                        {lv === 'rojo' ? 'Sin stock' : lv === 'amarillo' ? 'Bajo' : lv === 'verde' ? 'OK' : 'Sin niveles'}
                      </StatusPill>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {myLots.length > 0 && (
            <>
              <PanelTitle>Lotes (se usa primero el de arriba)</PanelTitle>
              <Table size="small">
                <TableHead><TableRow><TableCell>Lote</TableCell><TableCell>Material</TableCell><TableCell>Proveedor</TableCell><TableCell align="right">Quedan</TableCell></TableRow></TableHead>
                <TableBody>
                  {myLots.slice(0, 8).map(l => (
                    <TableRow key={l.id}>
                      <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{l.lotCode}</TableCell>
                      <TableCell>{l.materialName}</TableCell>
                      <TableCell>{l.supplierName || '—'}</TableCell>
                      <TableCell align="right">{fmtQty(l.remainingKg)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}

          {receptions.length > 0 && (
            <>
              <PanelTitle>Últimos ingresos</PanelTitle>
              <Stagger gap={0.75}>
                {receptions.map(r => <ReceptionRow key={r.id} r={r} />)}
              </Stagger>
            </>
          )}
        </>
      )}
    </PanelShell>
  );
};

const ReceptionRow: React.FC<{ r: Reception }> = ({ r }) => (
  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.75, borderBottom: `1px solid ${LINE}` }}>
    <PulseDot tone={r.remitoNumber ? 'ok' : 'warn'} size={8} />
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontWeight: 650, fontSize: 14 }} noWrap>{r.materialName} · {r.supplierName || 'Sin proveedor'}</Typography>
      <Typography variant="caption" sx={{ color: MUTED }} component="div" noWrap>
        {fmtWhen(r.receivedAt)} · {r.lotCode} · {r.remitoNumber ? `Comp. ${r.remitoNumber}` : 'Sin comprobante'}
        {r.differenceKg ? ` · dif. ${fmtQty(r.differenceKg)} kg` : ''}
      </Typography>
    </Box>
    <Typography sx={{ fontWeight: 750, color: TONES.ok.fg, whiteSpace: 'nowrap' }}>+{fmtQty(r.kg)} kg</Typography>
  </Stack>
);

// ==================== Mezclas ====================

const ORIGIN_COLORS: Record<string, { label: string; color: string }> = {
  Virgen: { label: 'Virgen', color: '#2F6BFF' },
  RecuperadaPropia: { label: 'Recuperada propia', color: '#16A36A' },
  RecuperadaComprada: { label: 'Recuperada comprada', color: '#E59A0C' },
  '': { label: 'Sin origen', color: '#B8BDC6' },
};

const Composition: React.FC<{ parts: { origin?: string | null; kg: number }[] }> = ({ parts }) => {
  const total = parts.reduce((a, p) => a + p.kg, 0) || 1;
  const by = new Map<string, number>();
  parts.forEach(p => by.set(p.origin || '', (by.get(p.origin || '') || 0) + p.kg));
  const segs = Object.keys(ORIGIN_COLORS).map(k => ({ k, ...ORIGIN_COLORS[k], kg: by.get(k) || 0 })).filter(s => s.kg > 0);
  return (
    <Box>
      <Box sx={{ display: 'flex', height: 8, borderRadius: 999, overflow: 'hidden', gap: '2px', bgcolor: '#EEE' }}>
        {segs.map((s, i) => (
          <Box key={s.k} sx={{ width: `${(s.kg / total) * 100}%`, bgcolor: s.color, transformOrigin: 'left', animation: `grow .7s ${i * 0.08}s both cubic-bezier(.16,1,.3,1)`, '@keyframes grow': { from: { transform: 'scaleX(0)' }, to: { transform: 'scaleX(1)' } } }} />
        ))}
      </Box>
      <Typography variant="caption" sx={{ color: MUTED }}>{segs.map(s => `${s.label} ${Math.round((s.kg / total) * 100)}%`).join(' · ')}</Typography>
    </Box>
  );
};

export const MixPanel: React.FC<PanelProps> = ({ zone, data, onClose }) => {
  const navigate = useNavigate();
  const week = data.mixes.filter(x => x.date.slice(0, 10) >= addDaysIso(data.today, -6));
  const kg = week.reduce((a, x) => a + x.totalKg, 0);
  const all = data.mixes.flatMap(x => x.components.map(c => ({ origin: c.materialOrigin, kg: c.kg })));
  return (
    <PanelShell
      title={zone.label}
      subtitle="Tandas de mezcla: cada una es un lote que va a la inyectora"
      onClose={onClose}
      footer={<Footer primary={{ label: 'Nueva mezcla', icon: <Blender />, onClick: () => navigate('/factory/materials') }} />}
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1.3fr 1fr' }, gap: 1, mt: 2 }}>
        <HeroFigure value={kg} unit="kg" caption="Mezclados en la semana" tone={kg ? 'accent' : 'neutral'} />
        <StatBox label="Tandas" value={week.length} hint="últimos 7 días" />
      </Box>
      {all.length > 0 && (
        <>
          <PanelTitle>Composición de las últimas 2 semanas</PanelTitle>
          <Composition parts={all} />
        </>
      )}
      <PanelTitle>Últimas mezclas</PanelTitle>
      {data.mixes.length ? (
        <Stagger>
          {data.mixes.slice(0, 8).map(x => (
            <Box key={x.id} sx={{ border: `1px solid ${LINE}`, borderRadius: 2, px: 1.75, py: 1.25 }}>
              <Stack direction="row" spacing={1} alignItems="baseline">
                <Typography sx={{ fontWeight: 700, flex: 1 }}>{x.code}</Typography>
                <Typography sx={{ fontWeight: 750 }}>{fmtKg(x.totalKg)}</Typography>
              </Stack>
              <Typography variant="body2" sx={{ color: MUTED, mb: 0.75 }}>
                {fmtDayShort(x.date)} · {[x.mixRecipeName, x.productName, x.machineName].filter(Boolean).join(' · ') || 'Sin receta'} · {x.shiftCount ? `en ${x.shiftCount} parte${x.shiftCount > 1 ? 's' : ''}` : 'sin usar todavía'}
              </Typography>
              <Composition parts={x.components.map(c => ({ origin: c.materialOrigin, kg: c.kg }))} />
            </Box>
          ))}
        </Stagger>
      ) : <Typography variant="body2" sx={{ color: MUTED }}>Sin mezclas en las últimas dos semanas.</Typography>}
    </PanelShell>
  );
};

// ==================== Triturado: molienda ====================

export const GrindingPanel: React.FC<PanelProps> = ({ zone, data, onClose, onSelect }) => {
  const open = data.grinding.filter(g => g.status === 'Enviado');
  const back = data.grinding.filter(g => g.status === 'Retornado');
  const outKg = open.reduce((a, g) => a + g.sentKg, 0);
  const sent = back.reduce((a, g) => a + g.sentKg, 0);
  const ret = back.reduce((a, g) => a + (g.returnedKg || 0), 0);
  const shrink = sent ? (ret - sent) / sent : null;
  const cost = data.grinding.reduce((a, g) => a + (g.cost || 0), 0);
  return (
    <PanelShell
      title={zone.label}
      subtitle="Molienda propia y afuera, pesada a la salida y al retorno"
      onClose={onClose}
      footer={<Footer primary={{ label: 'Pesar en la balanza', icon: <ScaleIcon />, onClick: () => onSelect('balanza') }} />}
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1.3fr 1fr 1fr' }, gap: 1, mt: 2 }}>
        <HeroFigure value={outKg} unit="kg" caption="En molienda ahora" tone={outKg ? 'accent' : 'neutral'} />
        <StatBox label="Merma promedio" value={shrink != null ? fmtPct(shrink, 1) : '—'} tone={shrink != null ? (shrink < -0.06 ? 'bad' : shrink < -0.03 ? 'warn' : 'ok') : undefined} hint="viajes que volvieron" />
        <StatBox label="Costo molienda" value={cost ? fmtMoney(cost) : '—'} hint="últimos 60 días" />
      </Box>
      <PanelTitle>Afuera</PanelTitle>
      {open.length ? (
        <Stagger>
          {open.map(g => {
            const days = Math.floor((Date.now() - new Date(g.sentAt).getTime()) / 86_400_000);
            return (
              <Stack key={g.id} direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.75, borderBottom: `1px solid ${LINE}` }}>
                <PulseDot tone={days > 7 ? 'warn' : 'info'} size={8} pulse />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 650, fontSize: 14 }}>{g.code} · {g.materialName || g.description}</Typography>
                  <Typography variant="caption" sx={{ color: MUTED }}>{g.supplierName || 'Propia'} · salió {fmtWhen(g.sentAt)}{days ? ` · hace ${days} d` : ''}</Typography>
                </Box>
                <Typography sx={{ fontWeight: 750 }}>{fmtKg(g.sentKg)}</Typography>
              </Stack>
            );
          })}
        </Stagger>
      ) : <Typography variant="body2" sx={{ color: MUTED }}>No hay material en molienda.</Typography>}
      {back.length > 0 && (
        <>
          <PanelTitle>Volvieron</PanelTitle>
          <Table size="small">
            <TableHead><TableRow><TableCell>Viaje</TableCell><TableCell align="right">Salió</TableCell><TableCell align="right">Volvió</TableCell><TableCell align="right">Merma</TableCell></TableRow></TableHead>
            <TableBody>
              {back.slice(0, 8).map(g => (
                <TableRow key={g.id}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{g.code}</TableCell>
                  <TableCell align="right">{fmtQty(g.sentKg)}</TableCell>
                  <TableCell align="right">{fmtQty(g.returnedKg)}</TableCell>
                  <TableCell align="right" sx={{ color: (g.differencePct ?? 0) < -6 ? TONES.bad.fg : undefined }}>{g.differencePct != null ? `${String(g.differencePct).replace('.', ',')} %` : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </PanelShell>
  );
};

// ==================== Matrices ====================

type MoldState = { tone: Tone; label: string };
function moldState(m: Machine): MoldState {
  if (m.status === 'FueraDeServicio') return { tone: 'bad', label: 'Fuera de servicio' };
  if (m.status === 'EnMantenimiento') return { tone: 'warn', label: 'En reparación' };
  if (m.mountedOnId) return { tone: 'ok', label: 'En uso' };
  return { tone: 'neutral', label: 'Disponible' };
}

export const MoldsPanel: React.FC<PanelProps> = ({ zone, data, onClose, onSaved }) => {
  const navigate = useNavigate();
  const list = molds(data);
  const [selId, setSelId] = useState<number | null>(list.find(m => m.mountedOnId)?.id ?? list[0]?.id ?? null);
  const sel = list.find(m => m.id === selId) ?? null;
  const [plans, setPlans] = useState<MaintenancePlan[]>([]);
  const [action, setAction] = useState<'mount' | 'unmount' | null>(null);
  const [target, setTarget] = useState<number | ''>('');
  const [location, setLocation] = useState('');
  const [toMaint, setToMaint] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hosts = data.machines.filter(m => !isMold(m));
  const productOf = (id: number) => data.shifts.find(s => s.moldId === id)?.productName;

  useEffect(() => {
    setAction(null); setError(null);
    if (!selId) { setPlans([]); return; }
    let alive = true;
    api.getMaintenancePlans(selId).then(p => { if (alive) setPlans(p); }).catch(() => { if (alive) setPlans([]); });
    return () => { alive = false; };
  }, [selId]);

  const counts = {
    total: list.length,
    inUse: list.filter(m => moldState(m).label === 'En uso').length,
    free: list.filter(m => moldState(m).label === 'Disponible').length,
    repair: list.filter(m => ['warn', 'bad'].includes(moldState(m).tone)).length,
  };
  const ov = sel ? data.overview.find(o => o.machineId === sel.id) : undefined;
  const cyclePlan = plans.find(p => p.frequencyType === 'Cycles') ?? plans[0];

  const run = async () => {
    if (!sel) return;
    setSaving(true); setError(null);
    try {
      if (action === 'mount' && target !== '') await api.mountMold(sel.id, Number(target));
      if (action === 'unmount') await api.unmountMold(sel.id, { location: location.trim() || undefined, toMaintenance: toMaint });
      setAction(null); setTarget(''); setLocation(''); setToMaint(false);
      onSaved();
    } catch (e) {
      setError(errorMessage(e, 'No se pudo guardar'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PanelShell
      title={zone.label}
      subtitle="Ubicación y estado de los moldes"
      onClose={onClose}
      footer={sel && <Footer
        primary={sel.mountedOnId
          ? { label: 'Retirar de máquina', icon: <Widgets />, onClick: () => setAction('unmount') }
          : { label: 'Montar en máquina', icon: <PrecisionManufacturing />, onClick: () => setAction('mount'), disabled: sel.status === 'FueraDeServicio' }}
        secondary={{ label: 'Ver historial', icon: <Schedule />, onClick: () => navigate('/factory/maintenance') }}
      />}
    >
      {!list.length ? (
        <Missing title="No hay matrices cargadas" hint="Cargá cada matriz en Producción → Máquinas con tipo “Matriz”: código, cavidades y ciclo." action={{ label: 'Cargar matrices', path: '/factory/production' }} />
      ) : (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 1, mt: 2 }}>
            <StatBox label="matrices" value={<AnimatedNumber value={counts.total} />} />
            <StatBox label="en uso" value={<AnimatedNumber value={counts.inUse} />} tone={counts.inUse ? 'ok' : undefined} />
            <StatBox label="disponibles" value={<AnimatedNumber value={counts.free} />} />
            <StatBox label="en reparación" value={<AnimatedNumber value={counts.repair} />} tone={counts.repair ? 'warn' : undefined} />
          </Box>

          <PanelTitle>Listado de matrices</PanelTitle>
          <Table size="small">
            <TableHead><TableRow><TableCell>Código / Producto</TableCell><TableCell>Ubicación</TableCell><TableCell>Estado</TableCell></TableRow></TableHead>
            <TableBody>
              {list.map(m => {
                const st = moldState(m);
                const on = m.id === selId;
                return (
                  <TableRow key={m.id} hover selected={on} onClick={() => setSelId(m.id)}
                    sx={{ cursor: 'pointer', '&.Mui-selected, &.Mui-selected:hover': { bgcolor: '#FFE9DD' } }}>
                    <TableCell sx={{ fontWeight: on ? 700 : 500 }}>{[m.code, m.name].filter(Boolean).join(' · ')}{productOf(m.id) && <Typography variant="caption" sx={{ display: 'block', color: MUTED }}>{productOf(m.id)}</Typography>}</TableCell>
                    <TableCell>{m.mountedOnName || m.location || '—'}</TableCell>
                    <TableCell><StatusPill tone={st.tone}>{st.label}</StatusPill></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {sel && (
            <Box sx={{ mt: 2.5, border: `1px solid ${LINE}`, borderRadius: 3, p: 2 }}>
              <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography sx={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em' }}>{[sel.code, sel.name].filter(Boolean).join(' · ')}</Typography>
                <StatusPill tone={moldState(sel).tone}>{moldState(sel).label}</StatusPill>
              </Stack>
              <Box sx={{ mt: 1.5 }}>
                <KeyValue rows={[
                  ['Ubicación actual', sel.mountedOnName || sel.location || '—'],
                  ['Produce', productOf(sel.id) || '—'],
                  ['Cavidades', sel.cavitiesTotal ? `${sel.cavitiesTotal} totales · ${sel.cavitiesUseful ?? sel.cavitiesTotal} activas` : '—'],
                  ['Ciclo', sel.cycleSeconds ? `${String(sel.cycleSeconds).replace('.', ',')} s` : '—'],
                  ['Ciclos acumulados', ov ? `${fmtQty(ov.cycles)} · según partes` : '—'],
                ]} />
              </Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
                <Build sx={{ fontSize: 18, color: MUTED }} />
                <Typography sx={{ fontWeight: 700 }}>Mantenimiento</Typography>
              </Stack>
              {cyclePlan ? (
                <Box sx={{ mt: 1 }}>
                  <KeyValue rows={[
                    ['Tarea', cyclePlan.task],
                    ['Último servicio', cyclePlan.lastDoneAt ? fmtDayShort(cyclePlan.lastDoneAt) : '—'],
                    ['Próximo preventivo', cyclePlan.frequencyType === 'Cycles' ? `a los ${fmtQty((cyclePlan.lastDoneUsage || 0) + cyclePlan.frequencyValue)} ciclos` : cyclePlan.nextDueDate ? fmtDayShort(cyclePlan.nextDueDate) : '—'],
                  ]} />
                  {cyclePlan.frequencyValue > 0 && (
                    <Box sx={{ mt: 1.25 }}>
                      {(() => {
                        const used = Math.max(0, cyclePlan.frequencyValue - cyclePlan.remaining);
                        const p = Math.min(100, (used / cyclePlan.frequencyValue) * 100);
                        return (
                          <>
                            <Stack direction="row" justifyContent="space-between"><Typography variant="body2" sx={{ color: MUTED }}>Restan {fmtQty(cyclePlan.remaining)} {cyclePlan.remainingUnit}</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{Math.round(p)} %</Typography></Stack>
                            <Box sx={{ mt: 0.5 }}><ProgressBar value={p} color={cyclePlan.dueStatus === 'vencido' ? TONES.bad.dot : cyclePlan.dueStatus === 'proximo' ? '#E59A0C' : ACCENT} /></Box>
                          </>
                        );
                      })()}
                    </Box>
                  )}
                </Box>
              ) : <Typography variant="body2" sx={{ color: MUTED, mt: 0.5 }}>Sin plan de mantenimiento: cargá limpieza o revisión cada tantos ciclos.</Typography>}

              {action && (
                <Box sx={{ mt: 2, bgcolor: SURFACE, borderRadius: 2, p: 1.75 }}>
                  {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
                  {action === 'mount' ? (
                    <TextField select fullWidth size="small" label="Máquina" value={target} onChange={e => setTarget(e.target.value === '' ? '' : Number(e.target.value))}>
                      {hosts.map(h => {
                        const busy = data.machines.find(x => isMold(x) && x.mountedOnId === h.id);
                        return <MenuItem key={h.id} value={h.id}>{h.name}{busy ? ` · tiene ${busy.code || busy.name} (se retira)` : ''}</MenuItem>;
                      })}
                    </TextField>
                  ) : (
                    <>
                      <TextField fullWidth size="small" label="Queda en" placeholder="Rack A-02, Matricería, Taller externo" value={location} onChange={e => setLocation(e.target.value)} />
                      <FormControlLabel sx={{ mt: 0.5 }} control={<Checkbox size="small" checked={toMaint} onChange={e => setToMaint(e.target.checked)} />} label="Va a mantenimiento o reparación" />
                    </>
                  )}
                  <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
                    <Button variant="contained" onClick={run} disabled={saving || (action === 'mount' && target === '')} sx={{ ...accentButton, py: 0.75 }}
                      startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}>
                      {action === 'mount' ? 'Montar' : 'Retirar'}
                    </Button>
                    <Button onClick={() => setAction(null)} sx={{ textTransform: 'none', color: MUTED }}>Cancelar</Button>
                  </Stack>
                </Box>
              )}
            </Box>
          )}
        </>
      )}
    </PanelShell>
  );
};

// ==================== En fabricación ====================

export const WipPanel: React.FC<PanelProps> = ({ zone, data, onClose }) => {
  const navigate = useNavigate();
  const today = shiftsToday(data);
  const byProduct = new Map<string, { good: number; scrap: number; machines: Set<string> }>();
  today.forEach(s => {
    const r = byProduct.get(s.productName) || { good: 0, scrap: 0, machines: new Set<string>() };
    r.good += s.goodQuantity; r.scrap += s.scrapQuantity;
    if (s.machineName) r.machines.add(s.machineName);
    byProduct.set(s.productName, r);
  });
  const rows = Array.from(byProduct.entries()).sort((a, b) => b[1].good - a[1].good);
  const good = today.reduce((a, s) => a + s.goodQuantity, 0);
  const locs = locationsOf(zone, data);
  return (
    <PanelShell
      title={zone.label}
      subtitle="Lo que salió hoy de las máquinas, antes de su stock"
      onClose={onClose}
      footer={<Footer primary={{ label: 'Registrar producción', icon: <PostAdd />, onClick: () => navigate('/factory/production') }} />}
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1.3fr 1fr' }, gap: 1, mt: 2 }}>
        <HeroFigure value={good} caption="Unidades hoy" tone={good ? 'accent' : 'neutral'} />
        <StatBox label="Partes cargados" value={today.length} hint={data.kpisToday?.efficiencyPct != null ? `eficiencia ${fmtPct(data.kpisToday.efficiencyPct)}` : undefined} />
      </Box>
      <PanelTitle>Por producto</PanelTitle>
      {rows.length ? (
        <Table size="small">
          <TableHead><TableRow><TableCell>Producto</TableCell><TableCell>Máquinas</TableCell><TableCell align="right">Buenas</TableCell></TableRow></TableHead>
          <TableBody>
            {rows.map(([name, r]) => (
              <TableRow key={name}><TableCell>{name}</TableCell><TableCell>{Array.from(r.machines).join(', ') || '—'}</TableCell><TableCell align="right">{fmtQty(r.good)}</TableCell></TableRow>
            ))}
          </TableBody>
        </Table>
      ) : <Typography variant="body2" sx={{ color: MUTED }}>Todavía no hay partes de hoy.</Typography>}
      {locs.length > 0 && (
        <>
          <PanelTitle>Zonas en fabricación</PanelTitle>
          <Stack spacing={1.25}>
            {locs.map(l => (
              <Box key={l.id}>
                <Stack direction="row" justifyContent="space-between"><Typography variant="body2" sx={{ fontWeight: 650 }}>{l.code}{l.name ? ` · ${l.name}` : ''}</Typography><Typography variant="body2" sx={{ color: MUTED }}>{fmtQty(l.occupied)}{l.capacity ? ` / ${fmtQty(l.capacity)}` : ''}</Typography></Stack>
                {l.occupancyPct != null && <Box sx={{ mt: 0.5 }}><ProgressBar value={l.occupancyPct * 100} height={6} /></Box>}
              </Box>
            ))}
          </Stack>
        </>
      )}
    </PanelShell>
  );
};

// ==================== Entregas ====================

export const DeliveryPanel: React.FC<PanelProps> = ({ zone, data, onClose }) => {
  const navigate = useNavigate();
  const orders = [...data.orders].sort((a, b) => b.balance - a.balance);
  const pending = orders.reduce((a, o) => a + Math.max(0, o.balance), 0);
  return (
    <PanelShell
      title={zone.label}
      subtitle="Órdenes de compra abiertas de clientes"
      onClose={onClose}
      footer={<Footer primary={{ label: 'Registrar entrega', icon: <LocalShipping />, onClick: () => navigate('/factory/customer-orders') }} />}
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1.3fr 1fr' }, gap: 1, mt: 2 }}>
        <HeroFigure value={pending} caption="Unidades por entregar" tone={pending ? 'accent' : 'neutral'} />
        <StatBox label="OC abiertas" value={orders.length} />
      </Box>
      <PanelTitle>Por orden</PanelTitle>
      {orders.length ? (
        <Stagger>
          {orders.slice(0, 10).map(o => (
            <Box key={o.id}>
              <Stack direction="row" justifyContent="space-between" spacing={1}>
                <Typography variant="body2" sx={{ fontWeight: 650 }} noWrap>{o.customerName} · {o.productName}</Typography>
                <Typography variant="body2" sx={{ color: MUTED, whiteSpace: 'nowrap' }}>faltan {fmtQty(o.balance)}</Typography>
              </Stack>
              <Typography variant="caption" sx={{ color: MUTED }}>OC {o.number} · {fmtQty(o.delivered)} de {fmtQty(o.quantity)}{o.lastDeliveryDate ? ` · última ${fmtDayShort(o.lastDeliveryDate)}` : ''}</Typography>
              <Box sx={{ mt: 0.5 }}><ProgressBar value={o.deliveredPct * 100} height={6} color={TONES.ok.dot} /></Box>
            </Box>
          ))}
        </Stagger>
      ) : <Typography variant="body2" sx={{ color: MUTED }}>No hay órdenes abiertas.</Typography>}
      <Typography variant="caption" sx={{ color: MUTED, display: 'block', mt: 2 }}>Próxima etapa: GPS de la camioneta y el repartidor marcando entregas desde el celular.</Typography>
    </PanelShell>
  );
};

// ==================== Oficina ====================

export const OfficePanel: React.FC<PanelProps> = ({ zone, data, onClose, onSaved }) => {
  const navigate = useNavigate();
  const pending = pendingDocs(data);
  const claims = data.receptions.filter(r => r.differenceTreatment && r.differenceTreatment !== 'Aceptada');
  const grindCost = data.grinding.reduce((a, g) => a + (g.cost || 0), 0);
  return (
    <PanelShell
      title={zone.label}
      subtitle="Lo que pesó la planta y administración tiene que cerrar"
      onClose={onClose}
      footer={<Footer primary={{ label: 'Ver recepciones', icon: <ReceiptLong />, onClick: () => navigate('/factory/materials') }} />}
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1, mt: 2 }}>
        <StatBox label="Sin comprobante" value={pending.length} tone={pending.length ? 'warn' : 'ok'} />
        <StatBox label="Diferencias abiertas" value={claims.length} tone={claims.length ? 'warn' : undefined} />
        <StatBox label="Molienda a pagar" value={grindCost ? fmtMoney(grindCost) : '—'} hint="últimos 60 días" />
      </Box>
      <PanelTitle>Recepciones pendientes de vincular</PanelTitle>
      {pending.length ? (
        <Stagger>{pending.map(r => <LinkDocRow key={r.id} r={r} onSaved={onSaved} />)}</Stagger>
      ) : <Typography variant="body2" sx={{ color: MUTED }}>Todas las recepciones tienen su comprobante.</Typography>}
      {claims.length > 0 && (
        <>
          <PanelTitle>Diferencias con el proveedor</PanelTitle>
          <Stagger gap={0.75}>
            {claims.map(r => (
              <Stack key={r.id} direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.75, borderBottom: `1px solid ${LINE}` }}>
                <PulseDot tone="warn" size={8} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 650, fontSize: 14 }} noWrap>{r.supplierName || 'Sin proveedor'} · {r.remitoNumber}</Typography>
                  <Typography variant="caption" sx={{ color: MUTED }}>{fmtWhen(r.receivedAt)} · {r.differenceTreatment === 'NotaCredito' ? 'pedir nota de crédito' : 'reclamo'} · pesado {fmtQty(r.kg)} / comprobante {fmtQty(r.documentKg)} kg</Typography>
                </Box>
                <Typography sx={{ fontWeight: 750, color: TONES.warn.fg, whiteSpace: 'nowrap' }}>{fmtQty(r.differenceKg)} kg</Typography>
              </Stack>
            ))}
          </Stagger>
        </>
      )}
    </PanelShell>
  );
};

const LinkDocRow: React.FC<{ r: Reception; onSaved: () => void }> = ({ r, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [number, setNumber] = useState('');
  const [kg, setKg] = useState('');
  const [treatment, setTreatment] = useState<DifferenceTreatment>('Reclamo');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const docKg = kg !== '' ? num(kg) : null;
  const diff = docKg != null ? r.kg - docKg : 0;
  const save = async () => {
    setSaving(true); setError(null);
    try {
      await api.linkReceptionDocument(r.id, { remitoNumber: number.trim(), documentKg: docKg, differenceTreatment: diff ? treatment : null });
      setOpen(false);
      onSaved();
    } catch (e) { setError(errorMessage(e, 'No se pudo vincular')); } finally { setSaving(false); }
  };
  return (
    <Box sx={{ border: `1px solid ${LINE}`, borderRadius: 2, px: 1.75, py: 1.25 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 650, fontSize: 14 }} noWrap>{r.materialName} · {r.supplierName || 'Sin proveedor'}</Typography>
          <Typography variant="caption" sx={{ color: MUTED }}>{fmtWhen(r.receivedAt)} · {r.lotCode} · {fmtQty(r.kg)} kg pesados</Typography>
        </Box>
        {!open && <Button size="small" onClick={() => setOpen(true)} sx={{ textTransform: 'none', color: ACCENT, fontWeight: 700 }}>Vincular</Button>}
      </Stack>
      {open && (
        <Box sx={{ mt: 1.25 }}>
          {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField size="small" label="Factura o remito" value={number} onChange={e => setNumber(e.target.value)} sx={{ flex: 1.4 }} />
            <TextField size="small" label="Kg del comprobante" value={kg} onChange={e => setKg(e.target.value)} sx={{ flex: 1 }} inputProps={{ inputMode: 'decimal' }} />
          </Stack>
          {docKg != null && Math.abs(diff) > 0.009 && (
            <TextField select size="small" fullWidth sx={{ mt: 1 }} label={`Diferencia ${fmtQty(diff)} kg`} value={treatment} onChange={e => setTreatment(e.target.value as DifferenceTreatment)}>
              <MenuItem value="Reclamo">Dejar reclamo al proveedor</MenuItem>
              <MenuItem value="NotaCredito">Pedir nota de crédito</MenuItem>
              <MenuItem value="Aceptada">Aceptar la diferencia</MenuItem>
            </TextField>
          )}
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button variant="contained" onClick={save} disabled={saving || !number.trim()} sx={{ ...accentButton, py: 0.6, fontSize: 14 }}>Guardar</Button>
            <Button onClick={() => setOpen(false)} sx={{ textTransform: 'none', color: MUTED }}>Cancelar</Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

// ==================== Próxima etapa ====================

export const SoonPanel: React.FC<PanelProps> = ({ zone, onClose }) => {
  const navigate = useNavigate();
  return (
    <PanelShell
      title={zone.label}
      subtitle="Este sector todavía no tiene datos en el sistema"
      badge={<StatusPill tone="neutral">Próxima etapa</StatusPill>}
      onClose={onClose}
      footer={zone.link && <Footer secondary={{ label: zone.link.label, icon: <ArrowForward />, onClick: () => navigate(zone.link!.path) }} />}
    >
      <PanelTitle>Qué va a mostrar</PanelTitle>
      <Stagger>
        {(zone.soon || []).map(t => (
          <Stack key={t} direction="row" spacing={1.25} alignItems="flex-start">
            <Box sx={{ mt: 0.75, width: 6, height: 6, borderRadius: '50%', bgcolor: ACCENT, flexShrink: 0 }} />
            <Typography>{t}</Typography>
          </Stack>
        ))}
      </Stagger>
    </PanelShell>
  );
};

// ==================== Resumen (nada seleccionado) ====================

export const SummaryPanel: React.FC<{ layout: PlantLayout; data: PlantData; statuses: Record<string, ZoneStatus>; onSelect: (id: string) => void; updatedAt: Date | null }> = ({
  layout, data, statuses, onSelect, updatedAt,
}) => {
  const alerts = layout.zones
    .filter(z => statuses[z.id] && (statuses[z.id].tone === 'bad' || statuses[z.id].tone === 'warn'))
    .sort((a, b) => (statuses[a.id].tone === 'bad' ? 0 : 1) - (statuses[b.id].tone === 'bad' ? 0 : 1));
  const running = layout.zones.filter(z => z.kind === 'machine' && statuses[z.id]?.active);

  const activity = useMemo(() => {
    const items: { key: string; at: string; icon: React.ReactNode; title: string; detail: string; zone?: string }[] = [];
    data.shifts.forEach(s => items.push({ key: `s${s.id}`, at: shiftAt(s), icon: <Factory />, title: `${s.machineName || 'Parte'} · +${fmtQty(s.goodQuantity)} ${s.productName}`, detail: `Turno ${s.shift}${s.operatorName ? ` · ${s.operatorName}` : ''}`, zone: layout.zones.find(z => z.kind === 'machine' && findMachine(z, data.machines)?.id === s.machineId)?.id }));
    data.receptions.forEach(r => items.push({ key: `r${r.id}`, at: r.receivedAt, icon: <ScaleIcon />, title: `Ingreso · +${fmtQty(r.kg)} kg ${r.materialName}`, detail: [r.supplierName, r.lotCode].filter(Boolean).join(' · '), zone: 'balanza' }));
    data.mixes.forEach(x => items.push({ key: `m${x.id}`, at: new Date(`${x.date.slice(0, 10)}T12:00:00-03:00`).toISOString(), icon: <Blender />, title: `Mezcla ${x.code} · ${fmtQty(x.totalKg)} kg`, detail: [x.mixRecipeName, x.productName].filter(Boolean).join(' · ') || 'Sin receta', zone: 'mezclado' }));
    data.grinding.forEach(g => items.push({ key: `g${g.id}`, at: g.returnedAt || g.sentAt, icon: <LocalShipping />, title: `${g.returnedAt ? 'Volvió de molienda' : 'Salió a molienda'} · ${fmtQty(g.returnedKg ?? g.sentKg)} kg`, detail: g.code, zone: 'triturado' }));
    return items.sort((a, b) => b.at.localeCompare(a.at)).slice(0, 12);
  }, [data, layout]);

  return (
    <PanelShell
      title="Planta en vivo"
      subtitle={updatedAt ? `Actualizado ${updatedAt.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })} · se refresca solo cada minuto` : 'Cargando…'}
    >
      <PanelTitle>Para mirar</PanelTitle>
      {alerts.length ? (
        <Stagger gap={0.75}>
          {alerts.map(z => (
            <Stack key={z.id} direction="row" spacing={1.5} alignItems="center" onClick={() => onSelect(z.id)}
              sx={{ cursor: 'pointer', bgcolor: TONES[statuses[z.id].tone].bg, borderRadius: 2, px: 1.75, py: 1.1, '&:hover': { filter: 'brightness(0.97)' } }}>
              <PulseDot tone={statuses[z.id].tone} pulse={statuses[z.id].tone === 'bad'} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 14.5 }}>{z.label}</Typography>
                <Typography variant="body2" sx={{ color: TONES[statuses[z.id].tone].fg }}>{statuses[z.id].label}</Typography>
              </Box>
              {statuses[z.id].metric && <Typography sx={{ fontWeight: 700, color: INK, whiteSpace: 'nowrap' }}>{statuses[z.id].metric}</Typography>}
              <ArrowForward sx={{ fontSize: 18, color: MUTED }} />
            </Stack>
          ))}
        </Stagger>
      ) : (
        <Callout tone="ok" icon={<CheckCircle fontSize="inherit" />} title="Nada urgente">Stock, matrices y mantenimiento sin alertas.</Callout>
      )}

      <PanelTitle>Máquinas produciendo hoy</PanelTitle>
      <Stack direction="row" flexWrap="wrap" useFlexGap gap={1}>
        {layout.zones.filter(z => z.kind === 'machine').map(z => {
          const on = running.includes(z);
          return (
            <Box key={z.id} onClick={() => onSelect(z.id)} sx={{
              cursor: 'pointer', px: 1.5, py: 0.75, borderRadius: 999, border: `1px solid ${on ? TONES.ok.dot : LINE}`,
              bgcolor: on ? TONES.ok.bg : '#fff', display: 'inline-flex', alignItems: 'center', gap: 0.75,
            }}>
              <PulseDot tone={statuses[z.id]?.tone ?? 'neutral'} size={8} pulse={on} />
              <Typography sx={{ fontSize: 13.5, fontWeight: 650 }}>{z.label.replace('Inyectora', 'Iny.')}</Typography>
              {statuses[z.id]?.metric && <Typography sx={{ fontSize: 12.5, color: MUTED }}>{statuses[z.id].metric}</Typography>}
            </Box>
          );
        })}
      </Stack>

      <PanelTitle>Actividad reciente</PanelTitle>
      {activity.length ? (
        <Stagger gap={0}>
          {activity.map(a => (
            <Stack key={a.key} direction="row" spacing={1.5} alignItems="center" onClick={() => a.zone && onSelect(a.zone)}
              sx={{ py: 1, borderBottom: `1px solid ${LINE}`, cursor: a.zone ? 'pointer' : 'default' }}>
              <Box sx={{ width: 34, height: 34, borderRadius: '50%', bgcolor: SURFACE, color: ACCENT, display: 'grid', placeItems: 'center', flexShrink: 0, '& svg': { fontSize: 18 } }}>{a.icon}</Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 650, fontSize: 14 }} noWrap>{a.title}</Typography>
                <Typography variant="caption" sx={{ color: MUTED }} component="div" noWrap>{fmtWhen(a.at)}{a.detail ? ` · ${a.detail}` : ''}</Typography>
              </Box>
            </Stack>
          ))}
        </Stagger>
      ) : <Typography variant="body2" sx={{ color: MUTED }}>Sin movimientos en los últimos días.</Typography>}
    </PanelShell>
  );
};
